'use client'

import type { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import type { PaymentMethodRow } from '@/scopes/payment/domain/policies/paymentMethodsView'
import type { PaymentOptions } from '@/scopes/payment/domain/types'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { createLocation } from '@/scopes/common/infrastructure/dom/location'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import { createAdyenPaymentActions } from '@/scopes/payment/domain/adyen/actions'
import { usePaymentContext } from '@/scopes/payment/domain/PaymentContext'
import { AdyenPaymentStatusValues } from '@/scopes/payment/domain/types'
import { createAdyenCheckoutClient } from '@/scopes/payment/infrastructure/adyen/adyenCheckoutClient'
import { createPaymentApiClient } from '@/scopes/payment/infrastructure/paymentApiClient'
import { paymentStore } from '@/scopes/payment/infrastructure/store'
import { usePaymentStore } from '@/scopes/payment/infrastructure/usePaymentStore'
import styles from './PaymentMethodDetails.module.css'

interface AdyenPaymentMethodDetailsProps {
  paymentOptions: PaymentOptions
  selectedAmountType: string
  selectedPaymentMethodType: PaymentMethod | null
  paymentMethodRow: PaymentMethodRow
  customDepositAmount: number | null
}

export function PaymentMethodDetails({
  paymentOptions,
  selectedAmountType,
  selectedPaymentMethodType,
  paymentMethodRow,
  customDepositAmount,
}: AdyenPaymentMethodDetailsProps) {
  const { bookingNumber, contextItemId, groupUserId, scope } = usePaymentContext()
  const { formatMessage } = useIntl()
  const analyticsClient = ga4analyticsClient
  const containerRef = React.useRef<HTMLDivElement | null>(null)

  const status = usePaymentStore(state => state.status)

  const dynamicValuesRef = React.useRef({ paymentOptions, selectedAmountType, selectedPaymentMethodType, paymentMethodRow, customDepositAmount })
  dynamicValuesRef.current = { paymentOptions, selectedAmountType, selectedPaymentMethodType, paymentMethodRow, customDepositAmount }

  const actions = React.useMemo(
    () =>
      createAdyenPaymentActions({
        apiClient: createPaymentApiClient({ contextItemId, groupUserId }),
        adyenClient: createAdyenCheckoutClient(),
        location: createLocation(),
        logger: createNewRelicLogger({ scope: 'payment' }),
        store: paymentStore,
        bookingNumber,
        contextItemId,
        groupUserId,
        scope,
        getDynamicValues: () => dynamicValuesRef.current,
        analyticsClient,
      }),
    [bookingNumber, contextItemId, groupUserId, scope, analyticsClient],
  )

  React.useEffect(() => {
    const element = containerRef.current

    if (!element) {
      return
    }

    void actions.paymentComponentMounted(element)

    return () => {
      actions.paymentComponentUnmounted()
    }
    // Remounting on selectedAmountType/customDepositAmount changes ensures the
    // Adyen component is rebuilt with fresh payment parameters instead of
    // continuing to display a pay button for the previously selected amount.
  }, [actions, selectedAmountType, customDepositAmount])

  const isLoading = status === AdyenPaymentStatusValues.LOADING || status === AdyenPaymentStatusValues.SUBMITTING

  return (
    <div className={styles.wrapper}>
      {isLoading && (
        <Typography className={styles.message} variant="body-s-regular-tight" aria-live="polite">
          {formatMessage({
            id: 'payment_scope_adyen_loading',
            defaultMessage: 'Preparing secure payment…',
          })}
        </Typography>
      )}

      <div ref={containerRef} id={paymentMethodRow.key} className={styles.checkout} data-status={status} />
    </div>
  )
}
